import {useQuery} from '@tanstack/react-query';
import {Outlet, RouterProvider, createRootRoute, createRoute, createRouter} from '@tanstack/react-router';
import {useTonWallet} from '@tonconnect/ui-react';
import {useEffect, useMemo, useState} from 'react';
import App from '@/components/app';
// import {TanStackRouterDevtools} from "@tanstack/router-devtools";
import MainLayout from '@/components/main-layout';
import Test from '@/components/test';
import {getTokensByWallet} from '@/components/tokens/api';
import type TokenMetadataItem from '@/components/tokens/types/token-metadata-item.ts';
import RouteName from '@/enums/route-name.ts';
import NftReward from '@/pages/nft-reward';
import Raffle from '@/pages/raffle';
import TokenPnl from '@/pages/token-pnl';
import SwapWidget from '@/pages/swap-widget';
import SwapWidgetOld from '@/pages/swap';
import {TanStackRouterDevtools} from '@tanstack/router-devtools';
import tokensMetadataMapping from '@/components/swap-widget-new/mapping/mapping/tokens-metadata-mapping.ts';

const Routes: React.FunctionComponent = () => {
  const [accountAddress, setAccountAddress] = useState<string | null>(null);
  const [tokensMetadataState, setTokensMetadataState] = useState<TokenMetadataItem[]>([]);
  const walletData: any = useTonWallet();

  useEffect(() => {
    if (walletData) {
      // console.log(walletData);
      setAccountAddress(walletData.account.address);
    }
  }, [walletData]);

  const tokensByWalletResult = useQuery({
    queryKey: ['tokens-by-wallet'],
    queryFn: () => getTokensByWallet(accountAddress as string),
    enabled: false,
  });

  useEffect(() => {
    if (accountAddress) {
      tokensByWalletResult.refetch();
    }
  }, [accountAddress]);

  // const { data: tokensMetadataResultData }: { data: any } = tokensMetadataResult;
  const {data: tokensByWalletData}: { data: any } = tokensByWalletResult;

  const {tokensMetadata, tokensSymbols}: {
    tokensMetadata: TokenMetadataItem[];
    tokensSymbols: string[]
  } = useMemo(() =>
    tokensByWalletData?.data
      ? tokensMetadataMapping(tokensByWalletData?.data?.data)
      : {tokensMetadata: [], tokensSymbols: []}, [tokensByWalletResult.data]);

  useEffect(() => {
    if (tokensMetadata !== tokensMetadataState) {
      setTokensMetadataState(tokensMetadata);
    }
  }, [tokensByWalletResult.data]);

  const rootRoute = createRootRoute({
    component: () => (
      <App>
        <MainLayout>
          <Outlet/>
        </MainLayout>
        {import.meta.env.MODE !== 'production' && <TanStackRouterDevtools/>}
      </App>
    ),
  });

  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: function Index() {
      return <NftReward tokensMetadata={tokensMetadata} tokensSymbols={tokensSymbols}/>;
    },
  });

  const swapRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `/${RouteName.Swap}`,
    component: function Index() {
      return <SwapWidget tokensMetadata={tokensMetadataState}/>;
    },
  });

  const swapWidgetRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `/${RouteName.SwapWidget}`,
    component: function Index() {
      return <SwapWidgetOld/>;
    },
  });

  const nftRewardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `/${RouteName.NftReward}`,
    component: function Index() {
      return <NftReward tokensMetadata={tokensMetadata} tokensSymbols={tokensSymbols}/>;
    },
  });

  const nftRewardWithCollectionAddressRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `/${RouteName.NftReward}/$collectionAddress`,
    component: function Index() {
      return <NftReward tokensMetadata={tokensMetadata} tokensSymbols={tokensSymbols}/>;
    },
  });

  const tokenPnlRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `/${RouteName.TokenPnl}`,
    component: function Index() {
      return <TokenPnl tokensMetadata={tokensMetadata}/>;
    },
  });

  const raffleRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `/${RouteName.Raffle}`,
    component: function Index() {
      return <Raffle/>;
    },
  });

  const testRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `/${RouteName.Test}`,
    component: function Index() {
      return <Test/>;
    },
  });

  const routeTree = rootRoute.addChildren([
    indexRoute,
    tokenPnlRoute,
    raffleRoute,
    nftRewardRoute,
    nftRewardWithCollectionAddressRoute,
    swapRoute,
    swapWidgetRoute,
    testRoute,
  ]);

  const router = createRouter({routeTree});

  return <RouterProvider router={router}/>;
};

export default Routes;
